'use client'

import { useState } from 'react'

// MUI Imports
import {
  CheckCircle as CheckCircleIcon,
  NewReleases as NewReleasesIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Update as UpdateIcon
} from '@mui/icons-material'
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Fade,
  LinearProgress,
  Snackbar,
  Stack,
  Typography,
  useTheme
} from '@mui/material'

import { useUpdateNotification } from '@/hooks/useUpdateNotification'

interface UpdateNotificationProps {
  checkInterval?: number

  enabled?: boolean

  anchorOrigin?: {
    vertical: 'top' | 'bottom'
    horizontal: 'left' | 'center' | 'right'
  }

  autoHideDuration?: number

  variant?: 'modern' | 'minimal' | 'elegant'
}

const UpdateNotification: React.FC<UpdateNotificationProps> = ({
  anchorOrigin = { vertical: 'bottom', horizontal: 'center' },

  variant = 'modern'
}) => {
  const [isReloading, setIsReloading] = useState(false)
  const theme = useTheme()

  const { hasUpdate, currentVersion, latestVersion, lastChecked, reloadApp } = useUpdateNotification()

  const handleReload = async () => {
    setIsReloading(true)

    // Tambahkan delay singkat untuk UX yang lebih baik
    setTimeout(() => {
      reloadApp()
    }, 800)
  }

  // Format waktu pengecekan terakhir
  const getLastCheckedText = () => {
    if (!lastChecked) return ''

    const now = new Date()
    const diff = now.getTime() - lastChecked.getTime()
    const minutes = Math.floor(diff / 60000)

    if (minutes < 1) return 'baru saja'
    if (minutes === 1) return '1 menit yang lalu'
    if (minutes < 60) return `${minutes} menit yang lalu`

    const hours = Math.floor(minutes / 60)

    if (hours === 1) return '1 jam yang lalu'
    if (hours < 24) return `${hours} jam yang lalu`

    return lastChecked.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getVersionShort = (version: string) => {
    return version?.split('-')[0] || version
  }

  // Modern variant (default, now compact and no close button)
  const ModernNotification = () => (
    <Card
      elevation={0}
      sx={{
        minWidth: 280,
        maxWidth: 340,
        borderRadius: 3,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.18)}`,
        border: `1px solid ${alpha('#fff', 0.08)}`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
          pointerEvents: 'none'
        }
      }}
    >
      <CardContent sx={{ p: 2.2, position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              background: alpha('#fff', 0.13),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <NewReleasesIcon sx={{ fontSize: '1.3rem', color: '#fff' }} />
          </Box>
          <Box>
            <Typography
              variant='subtitle1'
              fontWeight={700}
              sx={{ color: '#fff', fontSize: '1rem', letterSpacing: '-0.01em' }}
            >
              Update Tersedia
            </Typography>
            <Typography variant='caption' sx={{ color: alpha('#fff', 0.8), fontSize: '0.7rem' }}>
              Versi terbaru siap diinstal
            </Typography>
          </Box>
        </Box>

        {/* Version Info */}
        {(currentVersion || latestVersion) && (
          <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5 }}>
            {currentVersion && (
              <Chip
                size='small'
                label={`v${getVersionShort(currentVersion)}`}
                icon={<CheckCircleIcon />}
                sx={{
                  backgroundColor: alpha('#fff', 0.13),
                  color: alpha('#fff', 0.9),
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  '& .MuiChip-icon': {
                    color: alpha('#fff', 0.7),
                    fontSize: '0.8rem'
                  }
                }}
              />
            )}
            {latestVersion && (
              <>
                <Box
                  sx={{
                    width: 16,
                    height: 1,
                    background: alpha('#fff', 0.25),
                    borderRadius: 1
                  }}
                />
                <Chip
                  size='small'
                  label={`v${getVersionShort(latestVersion)}`}
                  icon={<UpdateIcon />}
                  sx={{
                    backgroundColor: theme.palette.warning.main,
                    color: theme.palette.warning.contrastText,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    boxShadow: `0 2px 6px ${alpha(theme.palette.warning.main, 0.25)}`,
                    '& .MuiChip-icon': {
                      color: theme.palette.warning.contrastText,
                      fontSize: '0.8rem'
                    }
                  }}
                />
              </>
            )}
          </Stack>
        )}

        {/* Description */}
        <Typography
          variant='body2'
          sx={{
            color: alpha('#fff', 0.92),
            lineHeight: 1.5,
            mb: 1.5,
            fontSize: '0.82rem'
          }}
        >
          Pembaruan aplikasi tersedia. Tekan tombol di bawah untuk memperbarui sekarang.
        </Typography>

        {/* Progress Bar (when reloading) */}
        {isReloading && (
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant='caption'
              sx={{
                color: alpha('#fff', 0.8),
                mb: 0.5,
                display: 'block',
                fontWeight: 600
              }}
            >
              Sedang memperbarui aplikasi...
            </Typography>
            <LinearProgress
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: alpha('#fff', 0.13),
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.light} 100%)`,
                  borderRadius: 3
                }
              }}
            />
          </Box>
        )}

        {/* Actions */}
        <Button
          fullWidth
          size='medium'
          variant='contained'
          onClick={handleReload}
          disabled={isReloading}
          startIcon={isReloading ? null : <RefreshIcon />}
          sx={{
            fontWeight: 700,
            letterSpacing: 0.5,
            borderRadius: 2,
            py: 1,
            background: '#fff',
            color: theme.palette.primary.main,
            boxShadow: `0 4px 12px ${alpha('#fff', 0.18)}`,
            '&:hover': {
              background: '#f8fafc',
              boxShadow: `0 8px 24px ${alpha('#fff', 0.25)}`,
              transform: 'translateY(-1px)'
            },
            '&:disabled': {
              background: alpha('#fff', 0.7),
              color: alpha(theme.palette.primary.main, 0.6)
            },
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        >
          {isReloading ? 'Memperbarui...' : 'Perbarui Sekarang'}
        </Button>

        {/* Footer Info */}
        {lastChecked && (
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${alpha('#fff', 0.08)}` }}>
            <Stack direction='row' alignItems='center' spacing={1}>
              <ScheduleIcon sx={{ fontSize: '0.8rem', color: alpha('#fff', 0.6) }} />
              <Typography
                variant='caption'
                sx={{
                  color: alpha('#fff', 0.7),
                  fontSize: '0.7rem'
                }}
              >
                Terakhir diperiksa: {getLastCheckedText()}
              </Typography>
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  )

  // Minimal variant (no close button, compact)
  const MinimalNotification = () => (
    <Alert
      severity='info'
      variant='filled'
      sx={{
        minWidth: 220,
        maxWidth: 320,
        borderRadius: 2,
        background: theme.palette.grey[900],
        color: '#fff',
        boxShadow: `0 6px 18px ${alpha(theme.palette.grey[900], 0.25)}`,
        border: `1px solid ${theme.palette.grey[700]}`,
        '& .MuiAlert-icon': {
          color: theme.palette.info.main
        }
      }}
      icon={<UpdateIcon />}
      action={
        <Button
          size='small'
          variant='contained'
          color='info'
          onClick={handleReload}
          disabled={isReloading}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {isReloading ? 'Loading...' : 'Update'}
        </Button>
      }
    >
      <Typography variant='subtitle2' fontWeight={600} gutterBottom>
        Update Tersedia
      </Typography>
      <Typography variant='body2' sx={{ opacity: 0.9 }}>
        Versi {getVersionShort(latestVersion || '')} siap diinstal
      </Typography>
    </Alert>
  )

  // Elegant variant (no close/dismiss, compact)
  const ElegantNotification = () => (
    <Card
      elevation={0}
      sx={{
        minWidth: 240,
        maxWidth: 340,
        borderRadius: 4,
        background: theme.palette.background.paper,
        boxShadow: `0 10px 30px ${alpha(theme.palette.grey[500], 0.1)}`,
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          borderRadius: '4px 4px 0 0'
        }}
      />

      <CardContent sx={{ p: 2 }}>
        <Stack direction='row' spacing={1.5} alignItems='flex-start'>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              background: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main
            }}
          >
            <UpdateIcon />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant='subtitle1' fontWeight={700} gutterBottom>
              Pembaruan Tersedia
            </Typography>

            <Typography variant='body2' color='text.secondary' sx={{ mb: 1.2, lineHeight: 1.5 }}>
              Versi terbaru aplikasi telah tersedia dengan peningkatan performa dan fitur baru.
            </Typography>

            {(currentVersion || latestVersion) && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant='caption' color='text.secondary' sx={{ mb: 0.5, display: 'block' }}>
                  Versi:
                </Typography>
                <Stack direction='row' spacing={1.2} alignItems='center'>
                  <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
                    {getVersionShort(currentVersion || '')} → {getVersionShort(latestVersion || '')}
                  </Typography>
                </Stack>
              </Box>
            )}

            {isReloading && <LinearProgress sx={{ mb: 1.2, borderRadius: 1 }} />}

            <Button
              variant='contained'
              onClick={handleReload}
              disabled={isReloading}
              startIcon={<RefreshIcon />}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                px: 2.5
              }}
            >
              {isReloading ? 'Memperbarui...' : 'Perbarui'}
            </Button>

            {lastChecked && (
              <Typography variant='caption' color='text.secondary' sx={{ mt: 1.2, display: 'block' }}>
                Diperiksa: {getLastCheckedText()}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )

  const getNotificationComponent = () => {
    switch (variant) {
      case 'minimal':
        return <MinimalNotification />
      case 'elegant':
        return <ElegantNotification />
      default:
        return <ModernNotification />
    }
  }

  return (
    <Snackbar
      open={hasUpdate}
      anchorOrigin={anchorOrigin}
      autoHideDuration={null}
      TransitionComponent={Fade}
      sx={{
        '& .MuiSnackbarContent-root': {
          padding: 0,
          backgroundColor: 'transparent',
          boxShadow: 'none'
        }
      }}
    >
      <Box>{getNotificationComponent()}</Box>
    </Snackbar>
  )
}

export default UpdateNotification
